import { HTTP_INTERCEPTORS, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { UserService } from '../services/user.service';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(private mUserService: UserService) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<Object>> {
        let authReq = req;
        const token = this.mUserService.getToken();
        if (token != null) {
            if (this.mUserService.isTokenExpired()) {
                return this.handle401Error(req, next);
            } else {
                authReq = this.addTokenHeader(req, token);
            }
        }

        return next.handle(authReq).pipe(catchError(error => {
            if (error instanceof HttpErrorResponse && !authReq.url.includes('user/login') && !authReq.url.includes('user/refresh-token') && error.status === 401) {
                return this.handle401Error(authReq, next);
            }

            return throwError(() => error);
        }));
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const token = this.mUserService.getRefreshToken();

            if (token) {
                return this.mUserService.refreshToken(token).pipe(
                    switchMap((token) => {
                        this.isRefreshing = false;

                        this.mUserService.saveToken(token.token);
                        this.mUserService.saveRefreshToken(token.refreshToken);
                        this.mUserService.saveUser(token.token);

                        this.refreshTokenSubject.next(token.token);

                        return next.handle(this.addTokenHeader(request, token.token));
                    }),
                    catchError((err) => {
                        this.isRefreshing = false;

                        this.mUserService.signOut();
                        return throwError(() => err);
                    })
                );
            }
        }

        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token)))
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token) });
    }
}

export const authInterceptorProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
];
